import DataTable from '@/components/table/data-table';
import useGetPrompts from '@/hooks/queries/prompts/useGetPrompts';
import { useGetUsers } from '@/hooks/queries/users';
import { useGetVariables } from '@/hooks/queries/variables';
import { useCohortVariables } from '@/hooks/queries/variables/cohort-variables';
import {
  useAllVariablesWithValues,
  useVariables,
} from '@/hooks/queries/variables/variables-v2';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { AddCohortVariablePanel } from '@/pages/variables/components/add-cohort-variable-panel';
import { AddDynamicVariablePanel } from '@/pages/variables/components/add-dynamic-variable-panel';
import { CohortVariablesTab } from '@/pages/variables/components/cohort-variables-tab';
import { UserValuesPanel } from '@/pages/variables/components/user-values-panel';
import { useUserStore } from '@/store/use-user-store';
import { TextVariablePayload } from '@/types/variable';
import { Variable as DynamicVariable } from '@/types/variables';
import {
  stripTenantVariablePrefix,
  toTenantVariableTitle,
} from '@/utils/promptUtils';
import { Icon } from '@iconify/react';
import {
  Avatar,
  Button,
  Chip,
  Input,
  Progress,
  Spinner,
  Tab,
  Tabs,
} from '@nextui-org/react';
import { useMemo, useState } from 'react';
import { AddVariablePanel } from './add-variables-panel';

type DisplayVariablesType = {
  id: number;
  title: string;
  sortTitle: string;
  value: string;
  parent_content_type: string;
  parent_object_id: number | null;
  resource_id: number;
};

export default function VariablesPage() {
  const [isSharedFilter, setIsSharedFilter] =
    useState<string>('user-variables');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingDynamicVariable, setEditingDynamicVariable] =
    useState<DynamicVariable | null>(null);
  const [editingTenantVariable, setEditingTenantVariable] =
    useState<TextVariablePayload | null>(null);
  const [editingCohortVariable, setEditingCohortVariable] =
    useState<DynamicVariable | null>(null);
  const [sourceFilter, setSourceFilter] = useState<
    'all' | 'profile' | 'conversation'
  >('all');
  const [activeTab, setActiveTab] = useState<string>('definitions');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isUserPanelOpen, setIsUserPanelOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [variableSearch, setVariableSearch] = useState('');

  const { hasSuperAdminPermissions } = useUserStore();
  const { tenantId } = useActiveTenant();

  const { data, isLoading, refetch } = useGetVariables(
    {
      filters: {
        is_shared: isSharedFilter === 'shared' ? true : false,
      },
    },
    isSharedFilter === 'tenant-variables'
  );

  const { data: variables, isLoading: isLoadingVariables } = useVariables({
    filters: {
      content_type: 'zenauth.zenuser',
    },
  });
  const { data: promptsData } = useGetPrompts();

  // User values data (only for super admin)
  const { data: userValuesData, isLoading: isLoadingUserValues } =
    useAllVariablesWithValues(
      {
        content_type: 'zenauth.zenuser',
        include_values: 'true',
        include_private: 'true',
      },
      hasSuperAdminPermissions
    );
  const { data: usersData, isLoading: isLoadingUsers } = useGetUsers(
    hasSuperAdminPermissions && tenantId ? tenantId : null
  );

  const { data: cohortVariablesData, refetch: refetchCohortVariables } =
    useCohortVariables(isSharedFilter === 'cohort-variables');

  // Prompt name lookup map
  const promptNameMap = useMemo(() => {
    const map = new Map<number, string>();
    (promptsData?.results || []).forEach((prompt: any) => {
      map.set(prompt.id, prompt.name);
    });
    return map;
  }, [promptsData?.results]);

  // Source filter counts
  const sourceCounts = useMemo(() => {
    const results = variables?.results || [];
    const profile = results.filter(
      (v: any) => v.props?.source === 'user_profile'
    ).length;
    const conversation = results.filter(
      (v: any) => v.props?.source === 'conversation'
    ).length;
    return { profile, conversation };
  }, [variables?.results]);

  // Filtered variables by source
  const filteredVariables = useMemo(() => {
    const results = variables?.results || [];
    if (sourceFilter === 'profile') {
      return results.filter((v: any) => v.props?.source === 'user_profile');
    }
    if (sourceFilter === 'conversation') {
      return results.filter((v: any) => v.props?.source === 'conversation');
    }
    return results;
  }, [variables?.results, sourceFilter]);

  // Search within filtered variables
  const searchedVariables = useMemo(() => {
    if (!variableSearch) return filteredVariables;
    const q = variableSearch.toLowerCase();
    return filteredVariables.filter(
      (v: any) =>
        v.name?.toLowerCase().includes(q) ||
        v.description?.toLowerCase().includes(q)
    );
  }, [filteredVariables, variableSearch]);

  // User values aggregation
  const userValuesAggregated = useMemo(() => {
    if (!usersData?.results || !userValuesData) return [];
    const entries = (userValuesData as any)?.results || [];
    const totalDefs = variables?.results?.length || 0;

    // Group entries by user_id
    const entriesByUser = new Map<number, any[]>();
    entries.forEach((entry: any) => {
      const list = entriesByUser.get(entry.user_id) || [];
      list.push(entry);
      entriesByUser.set(entry.user_id, list);
    });

    // Build user rows
    return usersData.results
      .map((item: any) => {
        const user = item.user || item;
        const userEntries = entriesByUser.get(user.id) || [];
        const populated = userEntries.filter(
          (e: any) =>
            e.value !== null && e.value !== undefined && e.value !== ''
        ).length;
        return {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          populated,
          total: totalDefs,
          entries: userEntries,
        };
      })
      .filter((u: any) => u.first_name || u.last_name || u.email);
  }, [usersData?.results, userValuesData, variables?.results?.length]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!userSearch) return userValuesAggregated;
    const q = userSearch.toLowerCase();
    return userValuesAggregated.filter(
      (u: any) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [userValuesAggregated, userSearch]);

  // Tenant variables table config (unchanged)
  const tableConfig = {
    columns: [
      {
        uid: 'id',
        name: 'ID',
        label: 'ID',
        sortable: true,
      },
      {
        uid: 'title',
        name: 'Variable Name',
        label: 'Variable Name',
        sortable: true,
        sortKey: 'sortTitle',
        renderCell: (variable: DisplayVariablesType) => (
          <span>
            <span className="text-default-400">tenant.</span>
            {stripTenantVariablePrefix(variable.title)}
          </span>
        ),
      },
      {
        uid: 'value',
        name: 'Value',
        label: 'Value',
        sortable: true,
        wrap: true,
        renderCell: (variable: DisplayVariablesType) => (
          <div className="max-h-[5.5rem] overflow-y-auto whitespace-pre-wrap break-words text-sm leading-[1.375rem] py-0.5">
            {variable.value ?? ''}
          </div>
        ),
      },
      {
        uid: 'parent_object_id',
        name: 'Parent Object ID',
        label: 'Resource ID',
        sortable: true,
        renderCell: (variable: any) => variable.parent_object_id ?? 'N/A',
      },
      {
        uid: 'created_by',
        name: 'Created By',
        label: 'Created By',
        renderCell: (variable: any) =>
          `${variable.created_by.first_name} ${variable.created_by.last_name}`,
      },
      {
        uid: 'created_at',
        name: 'Created At',
        label: 'Created At',
        renderCell: (variable: any) =>
          new Date(variable.created_at).toLocaleString(),
        sortable: true,
      },
      {
        uid: 'actions',
        name: 'Actions',
        label: 'Actions',
        renderCell: (variable: DisplayVariablesType) => (
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={() => {
              setEditingTenantVariable({
                id: variable.id,
                parent_content_type: variable.parent_content_type,
                parent_object_id: variable.parent_object_id,
                text_variables: [
                  {
                    title: variable.title,
                    value: variable.value,
                  },
                ],
              });
              setIsPanelOpen(true);
            }}
          >
            <Icon icon="akar-icons:edit" width="20" />
          </Button>
        ),
      },
    ],
    initialVisibleColumns: [
      'id',
      'title',
      'value',
      'parent_object_id',
      'created_by',
      'created_at',
      'actions',
    ],
    searchable: true,
    searchPlaceholder: 'Search variables...',
    searchKey: 'title',
    initialSortDescriptor: {
      column: 'title',
      direction: 'ascending' as const,
    },
    withoutBorder: false,
    layout: 'fixed' as const,
    disableMaxHeight: true,
    bottomContentPlacement: 'outside' as const,
    stickyHeader: false,
  };

  // New user variables table config
  const variablesTableConfig = {
    columns: [
      {
        uid: 'name',
        name: 'Name',
        label: 'Name',
        sortable: true,
        width: '40%',
        renderCell: (variable: any) => (
          <div className="flex flex-col py-1">
            <span className="text-sm font-semibold font-mono">
              {variable.name}
            </span>
            {variable.description && (
              <span className="text-tiny text-foreground-400">
                {variable.description}
              </span>
            )}
          </div>
        ),
      },
      {
        uid: 'type',
        name: 'Type',
        label: 'Type',
        sortable: true,
        renderCell: (variable: any) => (
          <Chip size="sm" variant="flat" className="bg-default-100">
            {(variable.type_display || variable.type || 'Text').replace(
              /\s*\(.*?\)\s*$/,
              ''
            )}
          </Chip>
        ),
      },
      {
        uid: 'source',
        name: 'Source',
        label: 'Source',
        sortable: true,
        renderCell: (variable: any) => {
          const source = variable?.props?.source;
          if (source === 'user_profile') {
            return (
              <Chip size="sm" variant="flat" color="primary">
                Profile
              </Chip>
            );
          }
          if (source === 'conversation') {
            return (
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: 'bg-purple-100',
                  content: 'text-purple-600',
                }}
              >
                Conversation
              </Chip>
            );
          }
          return <span className="text-foreground-400">—</span>;
        },
      },
      {
        uid: 'agents',
        name: 'Linked agents',
        label: 'Linked agents',
        renderCell: (variable: any) => {
          const promptIds: number[] = variable?.props?.prompts || [];
          if (promptIds.length === 0) {
            return <span className="text-foreground-400">—</span>;
          }
          const names = promptIds
            .map((id) => promptNameMap.get(id))
            .filter(Boolean);
          return (
            <span className="text-sm">
              {names.length > 0 ? names.join(', ') : '—'}
            </span>
          );
        },
      },
      {
        uid: 'actions',
        name: '',
        label: '',
        renderCell: (variable: any) => (
          <Button
            size="sm"
            variant="light"
            color="primary"
            onPress={() => {
              setEditingDynamicVariable(variable);
              setIsPanelOpen(true);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    initialVisibleColumns: ['name', 'type', 'source', 'agents', 'actions'],
    searchable: true,
    searchPlaceholder: 'Search variables...',
    searchKey: 'name',
    withoutBorder: false,
    layout: 'fixed' as const,
    disableMaxHeight: true,
    bottomContentPlacement: 'outside' as const,
    stickyHeader: false,
  };

  // Transform tenant variables data
  const transformedData = useMemo(
    () =>
      data?.results.flatMap((item) =>
        item.text_variables
          .map((variable) => ({
            ...variable,
            id: variable.id,
            resource_id: item.id,
            parent_content_type: item.parent_content_type,
            parent_object_id: item.parent_object_id,
            sortTitle: stripTenantVariablePrefix(variable.title),
          }))
          .filter(
            (variable) =>
              variable.parent_content_type !== 'zenauth.zenuser' &&
              variable.parent_content_type !== 'agentos.prompt'
          )
      ) || [],
    [data?.results]
  );

  const existingTenantVariableTitles = useMemo(
    () =>
      transformedData.map((variable) =>
        toTenantVariableTitle(stripTenantVariablePrefix(variable.title))
      ),
    [transformedData]
  );

  const existingCohortVariableNames = useMemo(
    () => (cohortVariablesData?.results ?? []).map((variable) => variable.name),
    [cohortVariablesData?.results]
  );

  if (isLoadingVariables) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const contentSpinner = (
    <div className="flex flex-1 items-center justify-center">
      <Spinner size="lg" />
    </div>
  );

  const infoBanner = (
    <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
      <Icon
        icon="mdi:information-outline"
        className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0"
      />
      <span className="text-sm text-blue-700">
        {hasSuperAdminPermissions ? (
          <>
            This tab manages variable definitions. To view a specific
            user&apos;s stored values, switch to the{' '}
            <strong>User values</strong> tab.
          </>
        ) : (
          'This panel manages variable definitions only. The actual values stored for each user are not visible to admins.'
        )}
      </span>
    </div>
  );

  const pillFilters = (
    <Tabs
      aria-label="Source filter"
      variant="light"
      selectedKey={sourceFilter}
      onSelectionChange={(key) =>
        setSourceFilter(String(key) as 'all' | 'profile' | 'conversation')
      }
      classNames={{
        tabList: 'bg-default-100 p-1 rounded-xl gap-0 w-fit',
        tab: 'h-8 px-4 text-sm',
        cursor: 'bg-white rounded-lg shadow-sm',
        panel: 'hidden',
      }}
    >
      <Tab key="all" title="All" />
      <Tab
        key="profile"
        title={
          <span>
            Profile{' '}
            <span className="text-xs opacity-60">{sourceCounts.profile}</span>
          </span>
        }
      />
      <Tab
        key="conversation"
        title={
          <span>
            Conversation{' '}
            <span className="text-xs opacity-60">
              {sourceCounts.conversation}
            </span>
          </span>
        }
      />
    </Tabs>
  );

  const variableDefinitionsContent = (
    <>
      <div className="flex justify-between mb-4 flex-col sm:flex-row">
        {pillFilters}
        <Input
          placeholder="Search variables..."
          size="sm"
          className="max-w-full sm:max-w-[200px] mt-2 sm:mt-0"
          startContent={
            <Icon icon="mdi:magnify" className="h-4 w-4 text-foreground-400" />
          }
          value={variableSearch}
          onChange={(e) => setVariableSearch(e.target.value)}
        />
      </div>
      {infoBanner}
      <DataTable
        config={{
          ...variablesTableConfig,
          searchable: false,
        }}
        data={searchedVariables}
      />
    </>
  );

  const userValuesContent =
    isLoadingUserValues || isLoadingUsers ? (
      contentSpinner
    ) : (
      <div>
        <div className="flex justify-between items-center mb-4 flex-col sm:flex-row">
          <span className="text-sm text-foreground-400">
            Select a user to view their stored variable values
          </span>
          <Input
            placeholder="Search by name or email..."
            size="sm"
            className="max-w-xs"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            startContent={
              <Icon
                icon="mdi:magnify"
                className="h-4 w-4 text-foreground-400"
              />
            }
          />
        </div>

        {/* User list table */}
        <div className="border border-default-200 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[2fr_2fr_2fr_auto] gap-4 px-4 py-3 bg-default-50 border-b border-default-200">
            <span className="text-xs text-foreground-400 uppercase tracking-wide font-medium">
              User
            </span>
            <span className="text-xs text-foreground-400 uppercase tracking-wide font-medium">
              Email
            </span>
            <span className="text-xs text-foreground-400 uppercase tracking-wide font-medium">
              Variables populated
            </span>
            <span className="w-24" />
          </div>

          {/* Rows */}
          {filteredUsers.length === 0 ? (
            <div className="px-4 py-8 text-center text-foreground-400 text-sm">
              No users found
            </div>
          ) : (
            filteredUsers.map((user: any) => {
              const initials =
                (user.first_name?.[0] || '') + (user.last_name?.[0] || '');
              const fullName =
                `${user.first_name} ${user.last_name}`.trim() || 'Unknown';
              const progress =
                user.total > 0 ? (user.populated / user.total) * 100 : 0;
              const progressColor =
                progress >= 100
                  ? 'success'
                  : progress >= 50
                    ? 'primary'
                    : 'default';

              return (
                <div
                  key={user.id}
                  className="grid grid-cols-[2fr_2fr_2fr_auto] gap-4 px-4 py-3 items-center border-b border-default-100 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={initials}
                      size="sm"
                      classNames={{
                        base: 'bg-primary/10 text-primary flex-shrink-0',
                        name: 'text-xs font-semibold',
                      }}
                    />
                    <span className="text-sm font-medium">{fullName}</span>
                  </div>
                  <span className="text-sm text-foreground-500">
                    {user.email}
                  </span>
                  <div className="flex items-center gap-3">
                    <Progress
                      size="sm"
                      radius="full"
                      value={progress}
                      color={progressColor as any}
                      className="max-w-[120px]"
                    />
                    <span className="text-sm text-foreground-500">
                      {user.populated} / {user.total}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="light"
                    color="primary"
                    className="w-24"
                    onPress={() => {
                      setSelectedUser(user);
                      setIsUserPanelOpen(true);
                    }}
                  >
                    View values
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>
    );

  const renderAddVariableButton = (className?: string) => (
    <Button
      color="primary"
      size="sm"
      className={className}
      onPress={() => {
        setEditingDynamicVariable(null);
        setEditingTenantVariable(null);
        setEditingCohortVariable(null);
        setIsPanelOpen(true);
      }}
      startContent={<Icon icon="mdi:plus" className="h-4 w-4" />}
    >
      {isSharedFilter === 'user-variables'
        ? 'User variable'
        : isSharedFilter === 'cohort-variables'
          ? 'Cohort Variable'
          : 'Tenant Variable'}
    </Button>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 p-2 sm:p-4 pb-0">
        {/* Page header */}
        <div className="flex justify-between items-start mb-2 flex-col sm:flex-row">
          <div className="w-full sm:w-auto">
            <div className="flex items-center justify-between gap-2 sm:justify-start px-0 sm:px-4 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-2xl font-semibold">Variables</h1>
                {hasSuperAdminPermissions && (
                  <Chip size="sm" color="warning" variant="flat">
                    Super Admin
                  </Chip>
                )}
              </div>
              {renderAddVariableButton('flex-shrink-0 sm:hidden')}
            </div>
            <p className="text-sm text-foreground-400 px-0 sm:px-4">
              Define what LiFT remembers about users and captures from
              conversations
            </p>
          </div>
          <div className="flex gap-4 items-center px-0 sm:px-4 pt-2 w-full sm:w-auto">
            <Tabs
              aria-label="Variable type filter"
              variant="light"
              selectedKey={isSharedFilter}
              onSelectionChange={(key) => setIsSharedFilter(String(key))}
              classNames={{
                tabList: 'bg-default-100 p-1 rounded-xl gap-0',
                tab: 'h-8 px-4 text-sm',
                cursor: 'bg-white rounded-lg shadow-sm',
                panel: 'hidden',
              }}
            >
              <Tab key="user-variables" title="User Variables" />
              <Tab key="tenant-variables" title="Tenant Variables" />
              <Tab key="cohort-variables" title="Cohort Variables" />
            </Tabs>
            {renderAddVariableButton('hidden sm:flex flex-shrink-0')}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="min-h-0 flex-1 overflow-y-auto p-2 sm:p-4 pt-4">
        <div className="flex min-h-full flex-col px-0 sm:px-4">
          {isSharedFilter === 'user-variables' && (
            <>
              {hasSuperAdminPermissions ? (
                /* Super Admin: Two-tab layout */
                <>
                  {/* Custom underlined section tabs */}
                  <div className="flex gap-6 border-b border-default-200 mb-6">
                    <button
                      type="button"
                      className={`pb-2 text-sm transition-colors ${
                        activeTab === 'definitions'
                          ? 'text-foreground font-semibold border-b-2 border-primary'
                          : 'text-foreground-400 hover:text-foreground-500'
                      }`}
                      onClick={() => setActiveTab('definitions')}
                    >
                      Variable definitions
                    </button>
                    <button
                      type="button"
                      className={`pb-2 text-sm transition-colors ${
                        activeTab === 'user-values'
                          ? 'text-foreground font-semibold border-b-2 border-primary'
                          : 'text-foreground-400 hover:text-foreground-500'
                      }`}
                      onClick={() => setActiveTab('user-values')}
                    >
                      User values
                    </button>
                  </div>

                  {activeTab === 'definitions' && variableDefinitionsContent}
                  {activeTab === 'user-values' && userValuesContent}
                </>
              ) : (
                /* Admin: Direct content without tabs */
                variableDefinitionsContent
              )}
            </>
          )}

          {isSharedFilter === 'tenant-variables' &&
            (isLoading ? (
              contentSpinner
            ) : (
              <DataTable config={tableConfig} data={transformedData} />
            ))}

          {isSharedFilter === 'cohort-variables' && (
            <CohortVariablesTab
              onEditVariable={(variable) => {
                setEditingCohortVariable(variable);
                setIsPanelOpen(true);
              }}
            />
          )}
        </div>
      </div>

      {/* Panels */}
      {isSharedFilter === 'user-variables' && (
        <AddDynamicVariablePanel
          open={isPanelOpen}
          onOpenChange={() => {
            setEditingDynamicVariable(null);
            setIsPanelOpen(false);
          }}
          editingVariable={editingDynamicVariable}
        />
      )}
      {isSharedFilter === 'tenant-variables' && (
        <AddVariablePanel
          isOpen={isPanelOpen}
          onClose={() => {
            setEditingTenantVariable(null);
            setIsPanelOpen(false);
          }}
          editingVariable={editingTenantVariable}
          existingVariableTitles={existingTenantVariableTitles}
          onSuccess={() => {
            setEditingTenantVariable(null);
            setIsPanelOpen(false);
            refetch();
          }}
        />
      )}
      {isSharedFilter === 'cohort-variables' && (
        <AddCohortVariablePanel
          isOpen={isPanelOpen}
          onClose={() => {
            setEditingCohortVariable(null);
            setIsPanelOpen(false);
          }}
          editingVariable={editingCohortVariable}
          existingVariableNames={existingCohortVariableNames}
          onSuccess={async () => {
            setEditingCohortVariable(null);
            setIsPanelOpen(false);
            await refetchCohortVariables();
          }}
        />
      )}

      {/* User values drill-down panel */}
      <UserValuesPanel
        isOpen={isUserPanelOpen}
        onClose={() => {
          setIsUserPanelOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        userEntries={selectedUser?.entries || []}
        variableDefinitions={variables?.results || []}
      />
    </div>
  );
}
